/**
 * SEO-утилиты для управления мета-тегами в SPA
 */

import { BRAND_NAME } from '@shared/contants'
import { Product } from '@shared/generated'

/**
 * Типы для SEO метаданных
 */
export interface MetaData {
  title?: string;
  description?: string;
  keywords?: string;
  ogTitle?: string;
  ogType?: string;
  ogSiteName?: string;
  ogDescription?: string;
  ogImage?: string;
  ogUrl?: string;
  canonicalUrl?: string;
  twitterCard?: 'summary' | 'summary_large_image' | 'app' | 'player';
  twitterTitle?: string;
  twitterDescription?: string;
  twitterImage?: string;
  noindex?: boolean;
}

/** Internal helpers */
const getBaseUrl = (): string => {
  const fromEnv = import.meta.env?.VITE_BASE_URL as string | undefined
  // Fallback to current origin in browser
  const origin = typeof window !== 'undefined' ? window.location.origin : ''
  const base = (fromEnv && fromEnv.trim()) || origin || ''
  return base.replace(/\/$/, '')
}

const isAbsoluteUrl = (u?: string | null): boolean => !!u && /^(https?:)?\/\//i.test(u)

const buildAbsoluteUrl = (pathOrUrl?: string | null): string => {
  if (!pathOrUrl) return getBaseUrl()
  if (isAbsoluteUrl(pathOrUrl)) return pathOrUrl
  const base = getBaseUrl()
  const path = pathOrUrl.startsWith('/') ? pathOrUrl : `/${pathOrUrl}`
  return `${base}${path}`
}

const setMetaTag = (attr: 'name' | 'property', key: string, content: string) => {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute(attr, key)
    document.head.appendChild(el)
  }
  el.setAttribute('content', content)
}

const setLinkTag = (rel: string, href: string) => {
  let el = document.head.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null
  if (!el) {
    el = document.createElement('link')
    el.setAttribute('rel', rel)
    document.head.appendChild(el)
  }
  el.setAttribute('href', href)
}

const setOg = (key: string, content?: string) => {
  if (!content) return
  setMetaTag('property', `og:${key}`, content)
}

const setTwitter = (key: string, content?: string) => {
  if (!content) return
  // Prefer name="twitter:*"; also update property variant if present
  setMetaTag('name', `twitter:${key}`, content)
  const propertyEl = document.head.querySelector(`meta[property="twitter:${key}"]`) as HTMLMetaElement | null
  if (propertyEl) propertyEl.setAttribute('content', content)
}

/**
 * Устанавливает метаданные для текущей страницы
 * @param metadata - объект с метаданными
 */
export const setPageMetadata = (metadata: MetaData): void => {
  const {
    title,
    description,
    keywords,
    ogTitle,
    ogType,
    ogSiteName,
    ogDescription,
    ogImage,
    ogUrl,
    canonicalUrl,
    twitterCard,
    twitterTitle,
    twitterDescription,
    twitterImage,
    noindex,
  } = metadata

  // Установка title
  if (title) {
    document.title = `${title} | ${BRAND_NAME}`
  }

  // Установка description
  if (description) {
    setMetaTag('name', 'description', description)
  }

  // Установка keywords
  if (keywords) {
    setMetaTag('name', 'keywords', keywords)
  }

  // Установка Open Graph метатегов
  setOg('title', ogTitle)
  setOg('type', ogType)
  setOg('site_name', ogSiteName)
  setOg('description', ogDescription)
  setOg('image', ogImage)
  // Default og:url to current page canonical if not provided
  const computedCanonical = canonicalUrl
    ? buildAbsoluteUrl(canonicalUrl)
    : (typeof window !== 'undefined'
      ? `${getBaseUrl()}${window.location.pathname}`
      : getBaseUrl())
  setOg('url', ogUrl || computedCanonical)

  // Установка канонического URL
  setLinkTag('canonical', computedCanonical)

  // Установка Twitter карточек на основе Open Graph
  setTwitter('card', twitterCard || (ogImage ? 'summary_large_image' : 'summary'))
  setTwitter('title', twitterTitle || ogTitle || title || '')
  setTwitter('description', twitterDescription || ogDescription || description || '')
  setTwitter('image', twitterImage || ogImage || '')

  // Robots noindex
  if (noindex) {
    setMetaTag('name', 'robots', 'noindex,nofollow')
  }
  else {
    // If previously set, revert to index,follow
    setMetaTag('name', 'robots', 'index,follow')
  }
}

/**
 * Создает JSON-LD объект для схемы Product
 * @param product - объект с данными о продукте
 * @returns Объект JSON-LD (готов к JSON.stringify)
 */
export const createProductJsonLd = (product: Product): Record<string, unknown> => {
  // Image priority: fullPreview -> preview -> galleryImages[0] -> images[0]
  const firstGallery = (product.galleryImages && product.galleryImages[0]?.url) || null
  const firstImage = (product.images && product.images[0]?.url) || null
  const rawImage = product.fullPreview?.url || product.preview?.url || firstGallery || firstImage || ''
  const image = rawImage ? buildAbsoluteUrl(rawImage) : undefined

  // Price: prefer default priceOffer, then product.price, then first priceOffers
  const defaultListOffer = product.priceOffers?.find?.(po => !!po?.isDefault)
  const price = product.priceOffer?.price
    || (typeof product.price === 'number' ? product.price : undefined)
    || defaultListOffer?.price
    || product.priceOffers?.[0]?.price
    || undefined

  const availability = (product.stock?.amount ?? 0) > 0 ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock'
  const brandName = product.brand?.name || BRAND_NAME || 'Тобыл'
  const productUrl = buildAbsoluteUrl(`/product/${product.slug || product.id}`)

  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product.name,
    description: product.short_description || undefined,
    image,
    sku: product.xid || product.id,
    mpn: product.id,
    brand: {
      '@type': 'Brand',
      name: brandName
    },
    offers: {
      '@type': 'Offer',
      url: productUrl,
      price,
      priceCurrency: 'KZT',
      availability,
      seller: {
        '@type': 'Organization',
        name: brandName
      }
    }
  }
}

/**
 * Создает JSON-LD объект для схемы BreadcrumbList
 * @param breadcrumbs - массив хлебных крошек
 * @returns Объект JSON-LD (готов к JSON.stringify)
 */
export const createBreadcrumbJsonLd = (
  breadcrumbs: Array<{ name: string; url: string }>
): Record<string, unknown> => {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbs.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: buildAbsoluteUrl(item.url)
    }))
  }
}

/**
 * Добавляет JSON-LD скрипт на страницу
 * @param jsonLd - строка с JSON-LD данными
 * @param id - уникальный идентификатор скрипта
 */
export const injectJsonLd = (jsonLd: string, id: string): void => {
  const existingScript = document.getElementById(id)
  if (existingScript) {
    existingScript.remove()
  }
  
  const script = document.createElement('script')
  script.type = 'application/ld+json'
  script.id = id
  script.innerHTML = jsonLd
  document.head.appendChild(script)
}
